import React from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@mantine/core';
import useHardwareFirstTrialTerms from '../../hooks/useHardwareFirstTrialTerms';
import * as types from '../../types';
import HardwareFirstTrialModal from './HardwareFirstTrialModal';
import styles from './HardwareFirstTrialToggle.module.scss';

interface HardwareFirstTrialToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  productCategory: types.ProductCategory;
  /** the tracker's list price, charged 30 days after setup; the terms link waits for it */
  listPriceInCents: number | undefined;
}

/**
 * The "try it free for 30 days" row for a hardware-first tracker: the offer and its terms link on the
 * left, the switch on the right. The caller owns the state and renders it only for an eligible shopper.
 */
export default function HardwareFirstTrialToggle({
  checked,
  onChange,
  productCategory,
  listPriceInCents,
}: HardwareFirstTrialToggleProps) {
  const { t } = useTranslation();
  const terms = useHardwareFirstTrialTerms(listPriceInCents);
  const title = t('hardware_first_trial_toggle_title');

  return (
    <div className={styles.container}>
      <div className={styles.text}>
        <span className={styles.title}>{title}</span>
        <span className={styles.sub}>
          {terms ? t('hardware_first_trial_toggle_sub_with_link') : t('hardware_first_trial_toggle_sub')}
          {terms && (
            <>
              {' '}
              <HardwareFirstTrialModal
                productCategory={productCategory}
                terms={terms}
                trigger={
                  <button className={styles.learnMore} type="button">
                    {t('learn_more')}
                  </button>
                }
              />
            </>
          )}
        </span>
      </div>
      <Switch
        aria-label={title}
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
        color="black"
        size="sm"
      />
    </div>
  );
}
